import React, { useState } from "react";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { useLeadSources } from "../../Hooks/useLeadSources";
import { Loader2 } from "lucide-react";
import { canAccess } from "../../utils/permissions";

export default function LeadSources() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  // ✅ Delete Modal State
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");

  const {
    leadSources = [],
    isLoading,
    createLeadSource,
    updateLeadSource,
    deleteLeadSource,
    isCreating,
    isUpdating,
    isDeleting, // لو موجود بالهوك عندك
  } = useLeadSources();

  const filtered = leadSources.filter((source) =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!canAccess(["view_categories", "view_quote"])) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm font-medium">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <LinkIcon className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
          Access Restricted
        </h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view lead sources.
        </p>
      </div>
    );
  }

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;

    createLeadSource(
      { name: newName.trim() },
      {
        onSuccess: () => {
          setIsAdding(false);
          setNewName("");
        },
      }
    );
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!editName.trim()) return;

    updateLeadSource(
      { id: editingId, data: { name: editName.trim() } },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditName("");
        },
      }
    );
  };

  // ✅ بدل window.confirm: افتح مودال
  const openDeleteModal = (source) => {
    setDeleteId(source.id);
    setDeleteName(source.name || "this lead source");
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setDeleteId(null);
    setDeleteName("");
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteLeadSource(deleteId, {
      onSuccess: () => closeDeleteModal(),
      // خليها مفتوحة لو صار error
      // onError: () => {}
    });
  };

  return (
    <section className="space-y-8 py-4">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Sources</h1>
          <p className="text-gray-600 mt-1">
            Manage where your leads are coming from
          </p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 w-full sm:w-64 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1665f6] focus:border-transparent outline-none transition-all shadow-sm"
            />
          </div>
          {canAccess("add_category") && (
            <button
              onClick={() => setIsAdding(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
            >
              <PlusIcon className="w-5 h-5" />
              Add Source
            </button>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
            <LinkIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500 font-medium">Total Sources</p>
            <p className="text-2xl font-bold text-gray-900">
              {leadSources.length}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-[#1665f6]" />
            <p className="text-gray-500 font-medium">Fetching lead sources...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <MagnifyingGlassIcon className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              No lead sources found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or add a new source
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="text-left px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="text-right px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((source) => (
                  <tr key={source.id} className="hover:bg-gray-50/50 transition">
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                      #{source.id}
                    </td>

                    <td className="px-6 py-4">
                      {editingId === source.id ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full max-w-xs px-3 py-1.5 border border-[#1665f6] rounded-lg text-sm focus:ring-2 focus:ring-[#1665f6]/20 outline-none"
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm font-bold text-gray-900">
                          {source.name}
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(source.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {editingId === source.id ? (
                          <>
                            <button
                              onClick={handleUpdate}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 transition shadow-sm"
                            >
                              {isUpdating ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-200 transition"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {canAccess("update_category") && (
                              <button
                                onClick={() => {
                                  setEditingId(source.id);
                                  setEditName(source.name);
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="Edit"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                            )}
                            {canAccess("delete_category") && (
                              <button
                                onClick={() => openDeleteModal(source)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {isAdding && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-[#1665f6] to-[#8830b8] p-6 text-white relative">
              <h2 className="text-2xl font-bold">New Lead Source</h2>
              <p className="text-blue-100/80 text-sm mt-1">
                Add a new channel to track your leads
              </p>
              <button
                onClick={() => setIsAdding(false)}
                className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAdd} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Source Name
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Google Ads, Facebook, Referrals"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1665f6] focus:border-transparent outline-none transition-all"
                  required
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsAdding(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-[2] px-6 py-3 bg-gradient-to-r from-[#1665f6] to-[#8830b8] text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Source"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Delete Confirm Modal */}
      {isDeleteOpen && canAccess("delete_category") && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={closeDeleteModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-red-600 p-6 text-white relative">
              <h2 className="text-2xl font-bold">Confirm Delete</h2>
              <p className="text-red-100/80 text-sm mt-1">
                This action can’t be undone.
              </p>
              <button
                onClick={closeDeleteModal}
                className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete{" "}
                <span className="font-bold text-gray-900">{deleteName}</span>?
              </p>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={Boolean(isDeleting)}
                  className="flex-[2] px-6 py-3 bg-red-600 text-white rounded-xl font-bold shadow-lg hover:bg-red-700 disabled:opacity-70 transition-all flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
